import { FigureSettingsDto } from "aic";
import { ComparatorTypeDto } from "components/annotations/interface";
import { defaultSettings } from "contexts/appsettings/defaultSettings";
import { REACT_APP_BACKENDURL, REACT_APP_CRYSTALURL } from "../../../config";
import { MaintenanceModeReturn, loadMaintenanceMode } from "../admin/loadMaintenanceMode";
import { getComparatorTypes } from "../annotations/getComparatorTypes";
import { getFigureTypeOptions } from "../figure/getFigureTypeOptions";
import { getBackEndVersion } from "../getBackEndVersion";
import { HelpLink, loadHelpLinks } from "../helplinks/loadHelpLinks";
import { getReportTypes } from "../hierarchy/getReportTypes";
import { IAxiosReturnSuccess } from "../interface";
import { StyleReadDto, getAllStyles } from "../styles/getAllStyles";
import { UserPropertiesDto, getMe } from "./getMe";

export interface AppSettingsLoaderData {
  allowedReportTypes: string[];
  backEndUrl: string;
  me: UserPropertiesDto;
  backEndVersion: string;
  crystalUrl: string;
  maintenanceMode: MaintenanceModeReturn;
  helpLinks: HelpLink[];
  styles: StyleReadDto[];
  annotationComparatorTypes: ComparatorTypeDto[];
  figureSettings: FigureSettingsDto;
}

/**
 * Loads the application settings.
 *
 * @returns Promise<AppSettingsLoaderData>
 */
export const appSettingsLoader = async (): Promise<AppSettingsLoaderData> => {
  const backEndUrl = REACT_APP_BACKENDURL.includes("_REACT_APP_BACKENDURL_")
    ? // istanbul ignore next
      defaultSettings.backEndUrl
    : REACT_APP_BACKENDURL;
  const me = getMe(backEndUrl);
  const crystalUrl = REACT_APP_CRYSTALURL.includes("_REACT_APP_CRYSTALURL_")
    ? // istanbul ignore next
      defaultSettings.crystalUrl
    : REACT_APP_CRYSTALURL;
  const backEndVersion = getBackEndVersion(backEndUrl);
  const maintenanceMode = loadMaintenanceMode(backEndUrl);
  const helpLinks = loadHelpLinks(backEndUrl);
  const styles = getAllStyles(backEndUrl);
  const annotationComparatorTypes = getComparatorTypes(backEndUrl);
  const figureSettings = getFigureTypeOptions(backEndUrl);
  const allowedReportTypes = getReportTypes(backEndUrl, null);

  const request = await Promise.all([
    me,
    backEndVersion,
    maintenanceMode,
    helpLinks,
    styles,
    annotationComparatorTypes,
    figureSettings,
    allowedReportTypes,
  ]);
  if (request.some((r) => r.success === false)) {
    throw new Error("App settings loader failed");
  }
  const ret = {
    allowedReportTypes: (request[7] as IAxiosReturnSuccess<string[]>).data,
    backEndUrl,
    crystalUrl,
    me: (request[0] as IAxiosReturnSuccess<UserPropertiesDto>).data,
    backEndVersion: (request[1] as IAxiosReturnSuccess<string>).data,
    maintenanceMode: (request[2] as IAxiosReturnSuccess<MaintenanceModeReturn>).data,
    helpLinks: (request[3] as IAxiosReturnSuccess<HelpLink[]>).data,
    styles: (request[4] as IAxiosReturnSuccess<StyleReadDto[]>).data,
    annotationComparatorTypes: (request[5] as IAxiosReturnSuccess<ComparatorTypeDto[]>).data,
    figureSettings: (request[6] as IAxiosReturnSuccess<FigureSettingsDto>).data,
  };
  return ret;
};
